import { Alert, Button, Card, Form, Input, InputNumber, Select, Space, Switch, Table, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from './components/EducationPageChrome';
import {
  EducationGradeSelect,
  EducationSessionSelect,
  EducationSubjectSelect,
} from './components/EducationLookupSelects';

type Policy = { id: string; name?: string; pack_code: string; version: string; status: string };
type Component = { id: string; code: string; name: string; maximum_marks?: number | null; weight_percent?: number | null };
type Scheme = {
  id: string;
  name: string;
  status: string;
  moderation_required?: number | boolean | null;
  components: Component[];
};

export default function AssessmentSchemePage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const policies = useQuery({
    queryKey: ['education-assessment-policies'],
    enabled: isNew,
    queryFn: async () => ((await api.get<Policy[]>('/education/policy-packs')).data || []).filter((p) => p.status === 'published'),
  });
  const scheme = useQuery({
    queryKey: ['education-assessment-scheme', id],
    enabled: Boolean(id),
    queryFn: async () => (await api.get<Scheme>(`/education/assessments/schemes/${id}`)).data,
  });
  const create = useMutation({
    mutationFn: (values: Record<string, unknown>) => api.post<{ id: string }>('/education/assessments/schemes', values),
    onSuccess: (res) => {
      message.success(t('saved'));
      const schemeId = res.data?.id;
      if (schemeId) navigate(`/education/assessments/schemes/${schemeId}`);
      else navigate('/education/assessments');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });
  const toggleModeration = useMutation({
    mutationFn: (moderationRequired: boolean) =>
      api.patch(`/education/assessments/schemes/${id}`, { moderation_required: moderationRequired }),
    onSuccess: () => {
      message.success(t('saved'));
      queryClient.invalidateQueries({ queryKey: ['education-assessment-scheme', id] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });
  const add = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      api.post(`/education/assessments/schemes/${id}/components`, values),
    onSuccess: () => {
      message.success(t('saved'));
      queryClient.invalidateQueries({ queryKey: ['education-assessment-scheme', id] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });
  return (
    <EducationPageChrome
      title={t(isNew ? 'newAssessmentScheme' : 'assessmentScheme')}
      extra={<Button onClick={() => navigate('/education/assessments')}>{t('back')}</Button>}
    >
      <Alert showIcon type="info" message={t('assessmentConfigNotice')} style={{ marginBottom: 16 }} />
      {isNew ? (
        <Card size="small" style={{ maxWidth: 760 }}>
          <Form layout="vertical" onFinish={(values) => create.mutate(values)}>
            <Form.Item name="name" label={t('name')} rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="policy_pack_id" label={t('policyPack')} rules={[{ required: true }]}>
              <Select options={(policies.data || []).map((p) => ({ value: p.id, label: `${p.name || p.pack_code} — ${p.version}` }))} />
            </Form.Item>
            <Form.Item name="session_id" label={t('session')} rules={[{ required: true }]}><EducationSessionSelect /></Form.Item>
            <Form.Item name="grade_level_id" label={t('grade')} rules={[{ required: true }]}><EducationGradeSelect /></Form.Item>
            <Form.Item name="subject_id" label={t('subject')} rules={[{ required: true }]}><EducationSubjectSelect /></Form.Item>
            <Form.Item name="moderation_required" label={t('assessmentModerationRequired')} valuePropName="checked">
              <Switch />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={create.isPending}>{t('save')}</Button>
          </Form>
        </Card>
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Card size="small" title={scheme.data?.name} loading={scheme.isLoading}>
            <Table rowKey="id" pagination={false} dataSource={scheme.data?.components || []} columns={[
              { title: t('code'), dataIndex: 'code' },
              { title: t('name'), dataIndex: 'name' },
              { title: t('maximumMarks'), dataIndex: 'maximum_marks', render: (v) => v ?? '—' },
              { title: t('weightPercent'), dataIndex: 'weight_percent', render: (v) => v ?? '—' },
            ]} />
          </Card>
          {scheme.data?.status === 'draft' ? (
            <Card size="small" title={t('assessmentModerationRequired')}>
              <Space>
                <Switch
                  checked={Number(scheme.data.moderation_required) === 1 || scheme.data.moderation_required === true}
                  loading={toggleModeration.isPending}
                  onChange={(checked) => toggleModeration.mutate(checked)}
                />
                <span>{t('assessmentModerationDraftOnlyHint')}</span>
              </Space>
            </Card>
          ) : null}
          {scheme.data?.status === 'draft' ? (
            <Card size="small" title={t('addAssessmentComponent')}>
              <Form layout="inline" onFinish={(values) => add.mutate(values)}>
                <Form.Item name="code" rules={[{ required: true }]}><Input placeholder={t('code')} /></Form.Item>
                <Form.Item name="name" rules={[{ required: true }]}><Input placeholder={t('name')} /></Form.Item>
                <Form.Item name="maximum_marks"><InputNumber min={0} placeholder={t('maximumMarks')} /></Form.Item>
                <Form.Item name="weight_percent"><InputNumber min={0} max={100} placeholder={t('weightPercent')} /></Form.Item>
                <Button type="primary" htmlType="submit" loading={add.isPending}>{t('add')}</Button>
              </Form>
            </Card>
          ) : null}
        </Space>
      )}
    </EducationPageChrome>
  );
}
